import { RouteUtils } from "@rsm-hcd/javascript-core";
import { NotebookConstants } from "constants/notebook-constants";
import { siteMap } from "internal-sitemap";
import PageRecord from "models/view-models/page-record";
import { useCallback } from "react";
import useCurrentIdentity from "utilities/hooks/use-current-identity";

interface UseGetPageRoute {
    getPageRoute: (page: PageRecord) => string | null;
}

export const useGetPageRoute = (): UseGetPageRoute => {
    const { currentIdentity } = useCurrentIdentity();
    const currentUserId = currentIdentity?.user?.id;

    const isOwner = useCallback(
        (page: PageRecord): boolean => {
            if (currentUserId == null) {
                return false;
            }

            return currentUserId === page.ownerId;
        },
        [currentUserId]
    );

    const buildAnonymousPageRoute = useCallback(
        (pageSharingId: string): string | null => {
            return (
                RouteUtils.getUrl(siteMap.public.sharedPages, {
                    pageGuid: pageSharingId,
                }) ?? null
            );
        },
        []
    );

    const buildAuthenticatedPageRoute = useCallback(
        (pageId: number | undefined, notebookId: number): string | null => {
            if (pageId == null) {
                return null;
            }

            const route = RouteUtils.getUrl(siteMap.notebooks.notebook.page, {
                notebookId: notebookId,
                pageId: pageId,
            });

            return route ?? null;
        },
        []
    );

    const getPageRoute = useCallback(
        (page: PageRecord) => {
            if (currentUserId == null) {
                return buildAnonymousPageRoute(page.sharingId);
            }

            let notebookId = NotebookConstants.SharedPageNotebookId;

            if (isOwner(page)) {
                notebookId = page.notebookId;
            }

            return buildAuthenticatedPageRoute(page.id, notebookId);
        },
        [
            buildAnonymousPageRoute,
            buildAuthenticatedPageRoute,
            currentUserId,
            isOwner,
        ]
    );

    return {
        getPageRoute: getPageRoute,
    };
};
